"use client";

import { useLocale, useTranslations } from "use-intl";

import { LockIcon } from "@/components/appIcons";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { offerMovementSignal } from "@/components/marketplace/deal-offers-utils";
import { topPriceIndicator } from "@/components/marketplace/price-indicator-chips";
import { cn } from "@/lib/utils";

function OfferRowMembersLabel({ className }: { className?: string }) {
  const t = useTranslations("shared.affiliates");
  return (
    <p
      className={cn(
        "inline-flex items-center gap-1.5 text-[13px] text-muted-foreground",
        className,
      )}
    >
      <LockIcon aria-hidden="true" className="text-sm" />
      {t("offerHistoryMembersOnly")}
    </p>
  );
}

/** One offer against its own price history, as a short label: the top indicator, or "stable". */
export function OfferRowHistorySignal({
  offer,
  className,
}: {
  offer: DealOffer;
  className?: string;
}) {
  const t = useTranslations("shared.affiliates");
  const locale = useLocale();

  if (!offer.priceInsightsVisible) return <OfferRowMembersLabel className={className} />;

  const signal = offerSignal(offer, t, locale);
  if (!signal) return null;
  const { Icon, label, tone } = signal;

  return (
    <span
      className={cn("inline-flex items-center gap-1 text-[13px] font-semibold", tone, className)}
    >
      {Icon && <Icon aria-hidden="true" className="shrink-0 text-sm" />}
      {label}
    </span>
  );
}

function offerSignal(
  offer: DealOffer,
  t: ReturnType<typeof useTranslations<"shared.affiliates">>,
  locale: string,
) {
  const indicator = topPriceIndicator(offer.priceIndicators);
  if (indicator)
    return { Icon: indicator.icon, label: t(indicator.labelKey), tone: indicator.textClassName };
  const movement = offerMovementSignal(offer, locale, t);
  if (movement?.kind !== "flat") return null;
  return { Icon: movement.meta.icon, label: t("priceStable"), tone: "text-muted-foreground" };
}
